// @ts-nocheck
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChevronRight, ChevronLeft, Archive, ArchiveRestore, Check, RotateCcw } from 'lucide-react';
import api from '../../../api/client';
import { listProposals } from '../../../api/proposalsApi';
import { isAiProposalsEnabled } from '../../../helpers/agentFeature';
import InboxActionRail from './InboxActionRail';
import LinkToCustomer from './LinkToCustomer';
import Button from '../../ui/button';
import { chLabel, fmtTime, fmtRel, stripHtml, deriveIntent, ChannelTag, IntentChip } from './InboxItems';
import { archiveThread, handleThread, isArchived, isHandled, isLinked, isAutomatedIntent, linkAvailable } from '../../../api/inboxThreads';
import {
  fetchPendingDrafts,
  approveDraft,
  dismissDraft,
  fetchInboxMessages,
  updateMessageStatus,
} from '../../../api/communications';
import ConversationThread from './ConversationThread';
import './inbox.css';

/* The customer profile modal used everywhere else in the app. Lazy so the
   inbox bundle doesn't pull the whole profile tab tree until someone opens it. */
const CustomerPopup = lazy(() => import('../../Profile/CustomerPopup'));

/* Unified inbox — ONE conversation per customer, all channels interleaved.
   Channel is a chip on each item, never a separate page.

   Stage 1: shell + SMS. The thread renderer is already channel-aware so
   email and call items slot in without reshaping anything.

   AI drafting is EXPLICIT-ONLY (button click). Nothing auto-drafts on thread
   open — the upstream assistant budget is shared and per-thread background
   summarisation would eat it. */

const CHANNELS = [
  { key: 'needs_reply', label: 'Needs reply' },
  { key: 'all', label: 'All' },
  { key: 'sms', label: 'SMS' },
  { key: 'email', label: 'Email' },
  { key: 'call', label: 'Calls' },
  { key: 'unread', label: 'Unread' },
  { key: 'automated', label: 'Automated' },
  { key: 'archived', label: 'Archived' },
];
// Automated noise (bot / no-reply / notification threads) stays out of the working
// views by default; it has its own chip. Backend stamp first, text markers second.
const isAutomatedRow = (r) => isAutomatedIntent(r?.intent) || deriveIntent(r)?.key === 'automated';
const sameConv = (a, b) => Boolean(a && b) && (a.conversation_key || a.thread_id || a.id) === (b.conversation_key || b.thread_id || b.id);

/* ---------------------------- main ---------------------------- */

export default function UnifiedInbox({ initialFilter = 'all' }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [active, setActive] = useState(null);
  // The thread pane is ConversationThread; this counter asks it to reload.
  const [threadKey, setThreadKey] = useState(0);
  const [filter, setFilter] = useState(initialFilter);
  // Party UUIDs the AI watcher has flagged (has a pending inbox_reply proposal).
  // Lets a thread show an "AI flagged" cue in context. Pilot-gated.
  const [watcherFlagged, setWatcherFlagged] = useState(() => new Set());
  useEffect(() => {
    if (!isAiProposalsEnabled()) return undefined;
    let alive = true;
    const fetchFlags = async () => {
      try {
        const ids = new Set();
        let cursor = null;
        // page through pending watcher inbox_reply proposals (usually one page)
        for (let i = 0; i < 10; i += 1) {
          const { data } = await listProposals({ status: 'pending', surface: 'watcher', rule: 'inbox_reply', cursor });
          (data?.proposals || []).forEach((p) => {
            const puid = p.subject?.party_universal_id || p.party_universal_id;
            if (puid) ids.add(puid);
          });
          cursor = data?.next || null;
          if (!cursor) break;
        }
        if (alive) setWatcherFlagged(ids);
      } catch { /* endpoint down / not pilot — no chips, no harm */ }
    };
    fetchFlags();
    window.addEventListener('ai-proposals:changed', fetchFlags);
    return () => { alive = false; window.removeEventListener('ai-proposals:changed', fetchFlags); };
  }, []);
  const [query, setQuery] = useState('');
  // "Needs reply": AI drafts awaiting a human. Per-reply approval only —
  // there is no bulk action here and the API has no bulk route.
  const [drafts, setDrafts] = useState([]);
  const [draftMeta, setDraftMeta] = useState({ stale_hidden_count: 0, freshness_days: 7 });
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [draftBody, setDraftBody] = useState('');
  const [draftBusy, setDraftBusy] = useState(false);
  // Context rail collapse — a per-user preference, so it sticks across sessions.
  const [ctxOpen, setCtxOpen] = useState(() => {
    try { return localStorage.getItem('ubx.ctxOpen') !== '0'; } catch { return true; }
  });
  const toggleCtx = useCallback(() => {
    setCtxOpen((v) => {
      const next = !v;
      try { localStorage.setItem('ubx.ctxOpen', next ? '1' : '0'); } catch { /* private mode */ }
      return next;
    });
  }, []);
  // The app-standard customer/lead profile modal, opened from the context card.
  const [profileFor, setProfileFor] = useState(null);
  // Link to customer / Create lead for an unlinked conversation (lane C's link leg).
  const [linkFor, setLinkFor] = useState(null);
  // Link to customer / Create lead show only once the link route exists on this API
  // (lane C's leg) — a button whose request 404s is worse than no button. Asked the
  // first time an UNLINKED thread is opened, not at mount: until the route is live the
  // probe is a 404, and a 404 on every inbox load is a console error on every screen.
  const [canLink, setCanLink] = useState(null); // null = not asked yet
  useEffect(() => {
    if (canLink !== null || !active || isLinked(active)) return undefined;
    let live = true;
    linkAvailable().then((ok) => { if (live) setCanLink(Boolean(ok)); });
    return () => { live = false; };
  }, [active, canLink]);
  const rootRef = useRef(null);

  /* The composer must ALWAYS be fully visible — the pane sizes itself to
     the space below whatever chrome sits above it, instead of guessing the
     chrome height in CSS (the guess clipped the composer on short windows). */
  const [paneHeight, setPaneHeight] = useState(null);
  useEffect(() => {
    const measure = () => {
      if (!rootRef.current) return;
      const top = rootRef.current.getBoundingClientRect().top + window.scrollY;
      setPaneHeight(Math.max(420, window.innerHeight - top - 16));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);


  const loadDrafts = useCallback(async () => {
    try {
      const d = await fetchPendingDrafts();
      setDrafts(Array.isArray(d.results) ? d.results : []);
      setDraftMeta({
        stale_hidden_count: d.stale_hidden_count || 0,
        freshness_days: d.freshness_days || 7,
      });
    } catch { /* queue is additive; never block the inbox */ }
  }, []);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  // Deep link from the owner alert SMS. ?draft=<id> when the id is known;
  // ?thread=<thread_id> from the alert itself, which fires BEFORE the draft
  // is written (so an AI failure can't starve the alert) — resolve it to
  // that thread's pending draft once the queue loads.
  const [deepLinkThread, setDeepLinkThread] = useState(null);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const id = q.get('draft');
    const thread = q.get('thread');
    if (id) { setFilter('needs_reply'); setActiveDraftId(Number(id)); }
    else if (thread) { setFilter('needs_reply'); setDeepLinkThread(thread); }
  }, []);

  useEffect(() => {
    if (!deepLinkThread || activeDraftId) return;
    const match = drafts.find((d) => d.thread_id === deepLinkThread);
    if (match) setActiveDraftId(match.id);
  }, [deepLinkThread, drafts, activeDraftId]);

  useEffect(() => {
    if (!activeDraftId) return;
    const d = drafts.find((x) => x.id === activeDraftId);
    if (d) setDraftBody(d.body || '');
  }, [activeDraftId, drafts]);

  const approve = async (draft) => {
    setDraftBusy(true);
    try {
      await approveDraft(draft.id, draftBody);
      toast.success('Reply sent');
      setActiveDraftId(null);
      loadDrafts(); loadList();
    } catch (e) {
      const d = e?.response?.data;
      // Honest failure: a blocked or undispatched approve leaves it pending.
      toast.error(d?.reason ? `Not sent — ${d.reason}` : (d?.detail || 'Not sent'));
      loadDrafts();
    } finally { setDraftBusy(false); }
  };

  const dismiss = async (draft) => {
    setDraftBusy(true);
    try {
      await dismissDraft(draft.id);
      toast.info('Dismissed — answer it yourself in the thread');
      setActiveDraftId(null);
      loadDrafts();
    } catch { toast.error('Could not dismiss'); }
    finally { setDraftBusy(false); }
  };

  // ?archived=false|true — lane C's contract (`all` exists too; the chips never need it).
  const archivedView = filter === 'archived';
  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await fetchInboxMessages({ archived: archivedView ? 'true' : 'false' });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Could not load conversations');
    } finally {
      setLoadingList(false);
    }
  }, [archivedView]);

  /* Archive / Done on any thread — list hover, thread header, keys e / d.
     Server state (lane C): archived_at / handled_at / handled_by on the row. */
  const patchRow = useCallback((row, patch) => {
    setRows((rs) => rs.map((x) => (sameConv(x, row) ? { ...x, ...patch } : x)));
    setActive((a) => (sameConv(a, row) ? { ...a, ...patch } : a));
  }, []);
  const archive = useCallback(async (row, archived = true) => {
    if (!row) return;
    try {
      await archiveThread(row, archived);
      const stamp = archived ? new Date().toISOString() : null;
      patchRow(row, { is_archived: archived, archived_at: stamp });
      if (archived !== archivedView) {
        // it leaves this view
        setRows((rs) => rs.filter((x) => !sameConv(x, row)));
        setActive((a) => (sameConv(a, row) ? null : a));
      }
      toast.success(archived ? 'Archived' : 'Back in the inbox');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Couldn’t archive this conversation');
    }
  }, [archivedView, patchRow]);
  const markDone = useCallback(async (row, handled = true) => {
    if (!row) return;
    try {
      const res = await handleThread(row, handled);
      patchRow(row, { handled_at: handled ? (res?.handled_at || new Date().toISOString()) : null, handled_by: handled ? (res?.handled_by ?? row.handled_by) : null });
      toast.success(handled ? 'Done' : 'Reopened');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Couldn’t mark this conversation done');
    }
  }, [patchRow]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (!active || (e.key !== 'e' && e.key !== 'd')) return;
      e.preventDefault();
      const cur = rows.find((x) => sameConv(x, active)) || active;
      if (e.key === 'e') archive(cur, !isArchived(cur));
      else markDone(cur, !isHandled(cur));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, rows, archive, markDone]);

  useEffect(() => { loadList(); }, [loadList]);

  const target = useMemo(() => {
    if (!active) return null;
    return {
      party_universal_id: active.party_universal_id || undefined,
      lead_id: active.lead_id || undefined,
      customer_id: active.customer_id || undefined,
      thread_id: active.thread_id || undefined,
    };
  }, [active]);

  const openConversation = useCallback((row) => {
    setActive(row);
    setThreadKey((k) => k + 1);
    if (row?.id && row.is_read === false) {
      updateMessageStatus(row.id, { is_read: true }).catch(() => {});
    }
  }, []);


  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const automated = isAutomatedRow(r);
      if (filter === 'automated') { if (!automated) return false; }
      else if (filter === 'archived') { /* the server already scoped the rows */ }
      else {
        // Automated threads stay out of the working views — "All" included shows
        // everything a person wrote; bots live under their own chip.
        if (automated) return false;
        if (filter === 'unread' && r.is_read !== false) return false;
        if (filter !== 'all' && filter !== 'unread') {
          const c = chLabel(r.channel);
          const match = filter === 'call' ? (c === 'call' || c === 'voicemail') : c === filter;
          if (!match) return false;
        }
      }
      if (!q) return true;
      return [r.contact_name, r.display_name, r.contact_phone, r.contact_email, r.body, r.subject]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rows, filter, query]);

  // Needs reply never shows a bot's thread: a draft on an automated inbound is hidden.
  const visibleDrafts = useMemo(() => drafts.filter((d) => !isAutomatedRow({ ...d, body: d.inbound?.body || d.body })), [drafts]);

  const rowName = (r) =>
    r.display_name || r.contact_name || r.contact_phone || r.contact_email || 'Unknown';

  /* Open the profile for the conversation's ACTUAL linked record. The bug this
     replaces (same class as the leads-popup fix): opening a popup that guesses
     its kind and falls through to the customer branch, showing/patching the
     wrong record. Here we key STRUCTURALLY off which FK the row carries — a
     resolved customer wins, else the lead — and stamp `object` + the matching
     id + universal_id so the popup resolves that exact record, never a guess. */
  const openProfile = useCallback((row) => {
    const r = row || active;
    if (!r) return;
    if (r.customer_id) {
      setProfileFor({
        kind: 'customer',
        rec: {
          object: 'customer',
          id: r.customer_id,
          customer_id: r.customer_id,
          universal_id: r.party_universal_id || undefined,
          display_name: rowName(r),
          contact_phone: r.contact_phone,
          contact_email: r.contact_email,
        },
      });
    } else if (r.lead_id) {
      setProfileFor({
        kind: 'lead',
        rec: {
          object: 'lead',
          id: r.lead_id,
          crm_lead_id: r.lead_id,
          universal_id: r.party_universal_id || undefined,
          display_name: rowName(r),
          contact_phone: r.contact_phone,
          contact_email: r.contact_email,
        },
      });
    } else {
      toast.info('No linked customer or lead on this conversation yet');
    }
  }, [active]);


  return (
    <div className={`ubx ${ctxOpen ? '' : 'ubx--ctx-collapsed'} ${active ? 'has-active' : ''}`} ref={rootRef} style={paneHeight ? { height: paneHeight } : undefined}>
      {/* ── conversations ── */}
      <div className="ubx-list">
        <div className="ubx-list-head">
          <input
            className="ubx-search"
            placeholder="Search conversations"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="ubx-chips">
            {CHANNELS.map((c) => (
              <button key={c.key}
                className={`ubx-chip ${filter === c.key ? 'on' : ''}`}
                onClick={() => setFilter(c.key)}>
                {c.label}
                {c.key === 'needs_reply' && visibleDrafts.length > 0 ? ` (${visibleDrafts.length})` : ''}
              </button>
            ))}
          </div>
        </div>
        <div className="ubx-rows">
          {filter === 'needs_reply' ? (
            <>
              {drafts.length === 0 ? (
                <div className="ubx-empty">
                  Nothing waiting. Replies needing a human land here.
                </div>
              ) : null}
              {visibleDrafts.map((d) => (
                <button
                  key={d.party_universal_id || d.thread_id || d.id}
                  className={`ubx-row ${active && (d.party_universal_id === active.party_universal_id || (d.thread_id && d.thread_id === active.thread_id)) ? 'on' : ''} unread`}
                  onClick={() => {
                    setActiveDraftId(d.id);
                    // Load the FULL thread into the middle pane — identical to
                    // the All tab. Needs-reply previously only set the draft
                    // body, so the thread history never rendered here.
                    openConversation({
                      party_universal_id: d.party_universal_id || undefined,
                      customer_id: d.customer_id || undefined,
                      lead_id: d.lead_id || undefined,
                      thread_id: d.thread_id || undefined,
                      contact_name: d.contact_name,
                      contact_phone: d.contact_phone,
                    });
                  }}
                >
                  <div className="ubx-row-top">
                    <span className="ubx-row-name">{d.contact_name || d.contact_phone || 'Unknown'}</span>
                    <IntentChip row={{ ...d, body: d.inbound?.body || d.body }} />
                    <span className="ubx-tag sms">{d.has_draft ? 'draft ready' : 'needs reply'}</span>
                    <span className="ubx-row-time">{fmtRel(d.created_at)}</span>
                  </div>
                  <span className="ubx-row-preview">
                    {d.inbound?.body ? `They said: ${d.inbound.body}` : 'Awaiting your reply'}
                  </span>
                </button>
              ))}
              {draftMeta.stale_hidden_count > 0 ? (
                <div className="ubx-empty" style={{ fontSize: 11 }}>
                  {draftMeta.stale_hidden_count} older draft
                  {draftMeta.stale_hidden_count === 1 ? '' : 's'} kept for audit —
                  not shown (older than {draftMeta.freshness_days} days).
                </div>
              ) : null}
            </>
          ) : null}
          {filter !== 'needs_reply' && loadingList ? <div className="ubx-empty">Loading…</div> : null}
          {filter !== 'needs_reply' && !loadingList && visibleRows.length === 0 ? (
            <div className="ubx-empty">No conversations match.</div>
          ) : null}
          {(filter === 'needs_reply' ? [] : visibleRows).map((r) => {
            const on = sameConv(active, r);
            return (
            <div key={r.conversation_key || r.thread_id || r.id} className={`ubx-row-wrap ${on ? 'on' : ''}`}>
            <button
              type="button"
              className={`ubx-row ${on ? 'on' : ''} ${r.is_read === false ? 'unread' : ''}`}
              onClick={() => openConversation(r)}
            >
              <div className="ubx-row-top">
                <span className="ubx-row-name">{rowName(r)}</span>
                {isHandled(r) ? <span className="ubx-done" title="Done" aria-label="Done"><Check size={12} /></span> : null}
                {r.party_universal_id && watcherFlagged.has(r.party_universal_id) ? (
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, letterSpacing: '0.03em', color: 'var(--warning)', border: '1px solid var(--warning)', borderRadius: 999, padding: '1px 6px' }}
                    title="The assistant flagged this conversation as needing a reply"
                  >
                    ⚡ AI flagged
                  </span>
                ) : null}
                <IntentChip row={r} />
                <ChannelTag channel={r.channel} />
                <span className="ubx-row-time">{fmtRel(r.timestamp || r.created_at)}</span>
              </div>
              <span className="ubx-row-preview">
                {r.direction === 'outbound' ? 'You: ' : ''}
                {r.subject ? `${r.subject} — ` : ''}
                {stripHtml(r.body) || '(no preview)'}
              </span>
            </button>
            <div className="ubx-row-actions">
              <button type="button" className="ubx-iconbtn" title={isHandled(r) ? 'Reopen (d)' : 'Done (d)'} aria-label={isHandled(r) ? 'Reopen' : 'Done'}
                onClick={(e) => { e.stopPropagation(); markDone(r, !isHandled(r)); }}>
                {isHandled(r) ? <RotateCcw size={14} /> : <Check size={14} />}
              </button>
              <button type="button" className="ubx-iconbtn" title={isArchived(r) ? 'Unarchive (e)' : 'Archive (e)'} aria-label={isArchived(r) ? 'Unarchive' : 'Archive'}
                onClick={(e) => { e.stopPropagation(); archive(r, !isArchived(r)); }}>
                {isArchived(r) ? <ArchiveRestore size={14} /> : <Archive size={14} />}
              </button>
            </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* ── thread ── */}
      <div className="ubx-thread">
        {filter === 'needs_reply' && activeDraftId ? (() => {
          const d = drafts.find((x) => x.id === activeDraftId);
          if (!d) return <div className="ubx-empty">Draft no longer pending.</div>;
          return (
            <>
              <div className="ubx-thread-head">
                <div style={{ minWidth: 0 }}>
                  <div className="ubx-thread-title">{d.contact_name || d.contact_phone || 'Unknown'}</div>
                  <div className="ubx-thread-sub">{d.contact_phone || ''}</div>
                </div>
              </div>
              <div className="ubx-msgs">
                {d.inbound ? (
                  <div className="ubx-msg in">
                    <div className="ubx-msg-meta">
                      <ChannelTag channel="sms" />
                      <span>{fmtTime(d.inbound.created_at)}</span>
                    </div>
                    <div className="ubx-bubble">{d.inbound.body}</div>
                  </div>
                ) : null}
                {d.campaign_goal ? (
                  <div className="ubx-targeting">
                    <b>Campaign goal:</b> {d.campaign_goal}
                  </div>
                ) : null}
              </div>
              <div className="ubx-composer">
                <div className="ubx-composer-top">
                  <span className="ubx-hint">AI suggested this reply — edit it, then you send it.</span>
                </div>
                <textarea value={draftBody} onChange={(e) => setDraftBody(e.target.value)} />
                <div className="ubx-composer-actions">
                  <span className="ubx-hint">
                    Nothing sends until you press Send. One reply at a time.
                  </span>
                  <button className="ubx-btn sm" disabled={draftBusy} onClick={() => dismiss(d)}>
                    Dismiss
                  </button>
                  <button className="ubx-btn primary" disabled={draftBusy || !draftBody.trim()}
                    onClick={() => approve(d)}>
                    {draftBusy ? 'Sending…' : 'Send reply'}
                  </button>
                </div>
              </div>
            </>
          );
        })() : !active ? (
          <div className="ubx-empty">Pick a conversation to see the full history.</div>
        ) : (
          <>
            <div className="ubx-thread-head">
              <div className="ubx-thread-lead">
              <Button variant="ghost" iconOnly className="ubx-back" aria-label="Back to conversations" icon={<ChevronLeft size={18} />} onClick={() => setActive(null)} />
              <div style={{ minWidth: 0 }}>
                {(active.customer_id || active.lead_id) ? (
                  // the name opens the customer window in place — the only way in at
                  // phone width, where the context panel is hidden
                  <button type="button" className="ubx-thread-title ubx-thread-title--link"
                    onClick={() => openProfile(active)} title="Open customer">
                    {rowName(active)}
                  </button>
                ) : (
                  <div className="ubx-thread-title">{rowName(active)}</div>
                )}
                <div className="ubx-thread-sub">
                  {[active.contact_phone, active.contact_email].filter(Boolean).join(' · ') || 'No contact details'}
                </div>
              </div>
              </div>
              <div className="ubx-thread-actions">
                <button className="ubx-btn sm" onClick={() => markDone(active, !isHandled(active))} title="d">
                  {isHandled(active) ? 'Reopen' : 'Done'}
                </button>
                <button className="ubx-btn sm" onClick={() => archive(active, !isArchived(active))} title="e">
                  {isArchived(active) ? 'Unarchive' : 'Archive'}
                </button>
                <button className="ubx-btn sm" onClick={() => setThreadKey((k) => k + 1)}>Refresh</button>
              </div>
            </div>

            <ConversationThread
              target={target}
              contact={{ name: rowName(active), phone: active.contact_phone, email: active.contact_email }}
              reloadKey={threadKey}
              onSent={loadList}
            />
          </>
        )}
      </div>

      {/* ── context rail (collapsible) ── */}
      <div className="ubx-ctx">
        <button
          className="ubx-ctx-collapse"
          onClick={toggleCtx}
          title={ctxOpen ? 'Collapse customer panel' : 'Expand customer panel'}
          aria-label={ctxOpen ? 'Collapse customer panel' : 'Expand customer panel'}
        >
          {ctxOpen ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        {ctxOpen ? (
          !active ? (
            <div className="ubx-empty">No conversation selected.</div>
          ) : (
            <>
              <div className="ubx-ctx-card">
                <div className="ubx-ctx-title">Customer</div>
                <div className="ubx-ctx-row"><span className="k">Name</span><span className="v">{rowName(active)}</span></div>
                {active.contact_phone ? (
                  <div className="ubx-ctx-row"><span className="k">Phone</span><span className="v">{active.contact_phone}</span></div>
                ) : null}
                {active.contact_email ? (
                  <div className="ubx-ctx-row"><span className="k">Email</span><span className="v">{active.contact_email}</span></div>
                ) : null}
                {(active.customer_id || active.lead_id) ? (
                  <button className="ubx-btn sm ubx-ctx-profile" onClick={() => openProfile(active)}>
                    View full profile
                  </button>
                ) : active.partner_prospect ? (
                  /* Partner prospect (Sales → Partner prospects): a transferred-audience
                     member who replied. Not a customer, not a lead until this tap. */
                  <div className="ubx-ctx-hint">
                    Partner prospect from <strong>{active.partner_prospect.source_tenant_name}</strong>
                    {active.partner_prospect.campaign_name ? ` · ${active.partner_prospect.campaign_name}` : ''}
                    <div style={{ marginTop: 8 }}>
                      <button
                        className="ubx-btn sm"
                        onClick={async () => {
                          try {
                            const r = await api.post(`/partner/members/${active.partner_prospect.member_id}/convert/`, {});
                            toast.success(`Lead created (${r.data?.attribution || 'partner'})`);
                            setActive({ ...active, lead_id: r.data?.lead_id, partner_prospect: { ...active.partner_prospect, status: 'converted', lead_id: r.data?.lead_id } });
                            loadList();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || 'Could not convert this prospect.');
                          }
                        }}
                      >
                        Make lead
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="ubx-ctx-hint">Not linked to a customer or lead yet</div>
                    {canLink === true ? (
                      <div className="ubx-ctx-actions">
                        <button className="ubx-btn sm" onClick={() => setLinkFor({ row: active, mode: 'link' })}>Link to customer</button>
                        <button className="ubx-btn sm" onClick={() => setLinkFor({ row: active, mode: 'create' })}>Create lead</button>
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              {isLinked(active) ? (
                <InboxActionRail active={active} onChanged={() => { loadList(); loadDrafts(); }} />
              ) : null}
              <div className="ubx-ctx-card">
                <div className="ubx-ctx-title">Jump to</div>
                <div className="ubx-ctx-actions">
                  <button className="ubx-btn sm" onClick={() => navigate('/schedule')}>Schedule</button>
                  <button className="ubx-btn sm" onClick={() => navigate('/quotes/create')}>New quote</button>
                </div>
              </div>
            </>
          )
        ) : null}
      </div>

      {linkFor ? (
        <LinkToCustomer
          row={linkFor.row}
          mode={linkFor.mode}
          onClose={() => setLinkFor(null)}
          onLinked={(linked) => {
            // The server re-keys the conversation by its new party: carry the new
            // conversation_key on the open thread so the reloaded list still matches it.
            patchRow(linkFor.row, linked);
            loadList();
          }}
        />
      ) : null}
      {profileFor ? (
        <Suspense fallback={null}>
          <CustomerPopup
            lead={profileFor.rec}
            leadType={profileFor.kind}
            onClose={() => { setProfileFor(null); loadList(); }}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
